import { CHART_RANGES, DATE_FORMATS, TIME_ZONES, formatDateTimePref } from "@strat/api";
import type { ChartLayerPrefs, ChartRangePref, DateFormat, TablePrefs, TimeZonePref } from "@strat/api";

import { Card, Field } from "../components/ui";
import { usePreferences } from "../prefs/PreferencesProvider";
import { useUserPrefs } from "../prefs/UserPrefsProvider";

/**
 * Settings → Display, Charts, Tables: written once and used by both the hub's
 * settings page and the desktop app's. Each change applies at once and is
 * saved where `useUserPrefs` keeps it — the account, the machine or the
 * browser — which `SavedWhere` says in words.
 */

const DATE_LABEL: Record<DateFormat, string> = {
  dmy: "Day / month / year",
  mdy: "Month / day / year",
  iso: "ISO (year-month-day)",
};
const ZONE_LABEL: Record<TimeZonePref, string> = {
  local: "This computer's time zone",
  utc: "UTC — the time bars open on",
};
const RANGE_LABEL: Record<ChartRangePref, string> = {
  auto: "About the last 300 bars (fits the timeframe)",
  "1d": "1 day",
  "1w": "1 week",
  "1m": "1 month",
  "3m": "3 months",
  "6m": "6 months",
  "1y": "1 year",
  all: "Everything",
};
const LAYERS: Array<[keyof ChartLayerPrefs, string, string]> = [
  ["entries", "Entries ▲", "Where each trade opened"],
  ["exits", "Exits ▼", "Where each trade closed"],
  ["tradeLines", "Trade lines", "A dotted line from each entry to its exit"],
  ["position", "Open position", "A live bot's open trade, from its entry to now"],
  ["stop", "Stop-loss", "A live bot's stop, while it is inside the price range on screen"],
  ["volume", "Volume", "The volume bars under the price"],
];

/** "Saved with your account", or wherever these choices actually live. */
export function SavedWhere() {
  const { scope, saveError } = useUserPrefs();
  if (saveError) return <p className="alert alert--error">Couldn't save your preferences: {saveError}</p>;
  return (
    <p className="muted small">
      {scope === "account"
        ? "Changes apply at once and are saved with your account, on every device you sign in on."
        : scope === "machine"
          ? "Changes apply at once and are saved on this computer."
          : "Changes apply at once and are kept in this browser. Sign in to keep them with your account."}
    </p>
  );
}

export function DisplaySettings() {
  const { prefs, update } = useUserPrefs();
  const { prefs: device, update: updateDevice } = usePreferences();
  const example = Date.UTC(2026, 9, 12, 14, 30);
  const setDisplay = (patch: Partial<typeof prefs.display>) =>
    update((p) => ({ ...p, display: { ...p.display, ...patch } }));
  return (
    <Card title="Display" id="display">
      <div className="settings-grid">
        <Field label="Theme">
          <div className="segmented" role="group" aria-label="Theme">
            {(["dark", "light"] as const).map((theme) => (
              <button
                key={theme}
                type="button"
                className={device.theme === theme ? "is-active" : undefined}
                aria-pressed={device.theme === theme}
                // The device theme drives the page; the provider copies it to the account.
                onClick={() => updateDevice({ theme })}
              >
                {theme === "dark" ? "Dark" : "Light"}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Dates">
          <select
            value={prefs.display.dateFormat}
            onChange={(e) => setDisplay({ dateFormat: e.target.value as DateFormat })}
          >
            {DATE_FORMATS.map((f) => (
              <option key={f} value={f}>
                {DATE_LABEL[f]} —{" "}
                {formatDateTimePref(example, { dateFormat: f, timeZone: "utc" }, { time: false })}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Times shown in">
          <select
            value={prefs.display.timeZone}
            onChange={(e) => setDisplay({ timeZone: e.target.value as TimeZonePref })}
          >
            {TIME_ZONES.map((z) => (
              <option key={z} value={z}>
                {ZONE_LABEL[z]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <p className="muted small">
        Now: <strong>{formatDateTimePref(Date.now(), prefs.display)}</strong>
      </p>
    </Card>
  );
}

export function ChartSettings() {
  const { prefs, update } = useUserPrefs();
  const setChart = (patch: Partial<typeof prefs.chart>) =>
    update((p) => ({ ...p, chart: { ...p.chart, ...patch } }));
  return (
    <Card title="Charts" id="charts">
      <p className="muted small">
        What every price chart shows — bots, the Workbench and backtest results. The filter bar above a chart
        changes the same choices.
      </p>
      <div className="settings-checks">
        {LAYERS.map(([key, label, hint]) => (
          <label key={key} className="checkbox" title={hint}>
            <input
              type="checkbox"
              checked={prefs.chart.layers[key]}
              onChange={(e) => setChart({ layers: { ...prefs.chart.layers, [key]: e.target.checked } })}
            />
            {label} <span className="muted small">· {hint}</span>
          </label>
        ))}
      </div>
      <Field label="A bot's chart opens on">
        <select
          value={prefs.chart.range}
          onChange={(e) => setChart({ range: e.target.value as ChartRangePref })}
        >
          {CHART_RANGES.map((r) => (
            <option key={r} value={r}>
              {RANGE_LABEL[r]}
            </option>
          ))}
        </select>
      </Field>
    </Card>
  );
}

function describeTable(table: TablePrefs): string {
  const parts: string[] = [];
  if (table.hidden?.length)
    parts.push(`${table.hidden.length} column${table.hidden.length === 1 ? "" : "s"} hidden`);
  if (table.sort) parts.push(`sorted by ${table.sort.key} ${table.sort.direction === "asc" ? "↑" : "↓"}`);
  const filters = Object.keys(table.filters ?? {}).length;
  if (filters) parts.push("default filters saved");
  return parts.join(" · ") || "nothing saved";
}

/**
 * Every table this person has arranged, with a way back to its defaults.
 * Columns are chosen on the table itself ("Columns ▾"), where you can see them.
 */
export function TableSettings({ labels = {} }: { labels?: Record<string, string> }) {
  const { prefs, update } = useUserPrefs();
  const ids = Object.keys(prefs.tables);
  const clear = (id: string | null) =>
    update((p) => {
      if (id === null) return { ...p, tables: {} };
      const tables = { ...p.tables };
      delete tables[id];
      return { ...p, tables };
    });
  return (
    <Card
      title="Tables"
      id="tables"
      actions={
        ids.length > 1 && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => clear(null)}>
            Reset all tables
          </button>
        )
      }
    >
      <p className="muted small">
        Pick a table's columns with its <strong>Columns ▾</strong> button; a table remembers how you last
        sorted it, and Explore → Bots keeps the filters you save as default.
      </p>
      {ids.length === 0 ? (
        <p className="muted">No table arranged yet — every table shows all its columns.</p>
      ) : (
        <ul className="settings-list">
          {ids.map((id) => (
            <li key={id}>
              <div>
                <strong>{labels[id] ?? id}</strong>
                <span className="muted small"> · {describeTable(prefs.tables[id]!)}</span>
              </div>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => clear(id)}>
                Reset
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function ResetPreferences() {
  const { reset } = useUserPrefs();
  const { update: updateDevice } = usePreferences();
  return (
    <Card title="Start over">
      <div className="row">
        <p className="muted" style={{ margin: 0 }}>
          Dates, charts, dashboard and tables back to how they come.
        </p>
        <button
          type="button"
          className="btn btn--danger"
          style={{ marginLeft: "auto" }}
          onClick={() => {
            if (!confirm("Reset all your display, chart, dashboard and table preferences?")) return;
            reset();
            updateDevice({ theme: "dark" });
          }}
        >
          Reset preferences
        </button>
      </div>
    </Card>
  );
}

/**
 * The desktop app's Settings page. The hub's adds its account, dashboard,
 * tables and alerts sections — those are hub pages; the desktop has none of them.
 */
export function SettingsPage() {
  return (
    <div className="stack narrow">
      <div className="page__head">
        <div className="page__title">
          <h2>Settings</h2>
          <SavedWhere />
        </div>
      </div>
      <DisplaySettings />
      <ChartSettings />
      <ResetPreferences />
    </div>
  );
}
